import { emptyWhereaboutsDay, UsersWhereaboutsDay } from '@features/whereabouts/models/movements.models';
import { Moment } from "moment";
import { IconPack } from '@shared/models/company.models';
import { FreeBusyStatus, getDefaultWhereaboutsKeyFromId, WhereaboutsOption } from '@features/whereabouts/services/WhereaboutOptions';
import { DATE_FORMAT } from "./DateUtils";

export function iconPackPath(iconPack: IconPack, imageName: string, freeBusyStatus: FreeBusyStatus): string {
  if (iconPack === IconPack.STANDARD) {
    return `/assets/icons/${iconPackToPath(iconPack)}/${imageName}`;
  }

  const prefixedImageName = imageName.replace('.svg', `${freeBusyStatusToPrefix(freeBusyStatus)}.svg`)
  return `/assets/icons/${iconPackToPath(iconPack)}/${prefixedImageName}`;
}

function iconPackToPath(iconPack: IconPack) {
  switch (iconPack) {
    case IconPack.STANDARD: return 'standard'
    case IconPack.BASIC: return 'basic';
    default: return 'standard';
  }
}

function freeBusyStatusToPrefix(freeBusyStatus: FreeBusyStatus) {
  switch (freeBusyStatus) {
    case FreeBusyStatus.FREE: return '-Available';
    case FreeBusyStatus.WORKING_ELSEWHERE: return '-Busy';
    case FreeBusyStatus.OOF: return '-OutOffice';
    case FreeBusyStatus.BUSY: return '-OutOffice';
    case FreeBusyStatus.TENTATIVE: return '-Busy';
    default: return '-Available';
  }
}

export function getWhereaboutsDayWithFallback(date: Moment, userId: string, whereaboutsDays: UsersWhereaboutsDay[]): UsersWhereaboutsDay {
  const searchDate = date.format(DATE_FORMAT);
  const day = whereaboutsDays.find(wd => wd.date === searchDate);
  if (day) {
    return day;
  } else {
    return emptyWhereaboutsDay(searchDate, userId)
  }
}

export function isStaticWhereabouts(whereabouts: any): boolean {
  if (whereabouts.key === 'HOME') return true;
  else if (whereabouts.key === 'NOT_WORKING') return true;
  else if (whereabouts.key === 'COMPANY_HOLIDAY') return true;
  else if (whereabouts.key === 'HOLIDAY') return true;
  else if (whereabouts.key === 'HOLIDAY_APPROVED') return true;
  else if (whereabouts.key === 'HOLIDAY_REJECTED') return true;
  else if (whereabouts.key === 'HOLIDAY_PENDING') return true;
  else if (whereabouts.key === 'UNKNOWN') return true;
  else if (whereabouts.key === 'OFFICE') return true;
  else if (whereabouts.key === 'HOME') return true;
  else if (whereabouts.key === 'NOT_WORKING') return true;
  return false;
}

export function getTranslationKey(whereabouts: any): string {
  if (whereabouts.id > 10) {
    return whereabouts.label;
  }
  if (whereabouts.key === 'HOME') return `whereabouts-labels.${whereabouts.key}`;
  else if (whereabouts.key === 'NOT_WORKING') return `whereabouts-labels.${whereabouts.key}`;
  else if (whereabouts.key === 'COMPANY_HOLIDAY') return `whereabouts-labels.${whereabouts.key}`;
  else if (whereabouts.key === 'HOLIDAY') return `whereabouts-labels.${whereabouts.key}`;
  else if (whereabouts.key === 'HOLIDAY_APPROVED') return `whereabouts-labels.${whereabouts.key}`;
  else if (whereabouts.key === 'HOLIDAY_REJECTED') return `whereabouts-labels.${whereabouts.key}`;
  else if (whereabouts.key === 'HOLIDAY_PENDING') return `whereabouts-labels.${whereabouts.key}`;
  else if (whereabouts.key === 'UNKNOWN') return `whereabouts-labels.${whereabouts.key}`;
  else if (whereabouts.key === 'OFFICE') return `whereabouts-labels.${whereabouts.key}`;
  return whereabouts.label;
}

export function getIdFromKeyIfDefaultWhereaboutsOverridden(whereaboutsOptions: WhereaboutsOption[], whereaboutsId: number): number {
  if (whereaboutsId < 10) {
    const key = getDefaultWhereaboutsKeyFromId(whereaboutsId);
    if (key) {
      const matchedWhereaboutsOption = whereaboutsOptions.find(wo => wo.key === key);
      if (matchedWhereaboutsOption) {
        return matchedWhereaboutsOption.id;
      }
    }
  }

  return whereaboutsId;
}

// A day can hold a fixed default id (< 10) while the company has overridden that
// option (same key, different id) — resolve by id, falling back to the default's key.
export function findWhereaboutsOptionById(whereaboutsOptions: WhereaboutsOption[], whereaboutsId: number): WhereaboutsOption | undefined {
  const resolvedId = getIdFromKeyIfDefaultWhereaboutsOverridden(whereaboutsOptions, whereaboutsId);
  return whereaboutsOptions.find(wo => wo.id === resolvedId);
}
